import React, { useState, useCallback } from 'react';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolid,
  HandThumbDownIcon as HandThumbDownSolid,
} from '@heroicons/react/24/solid';
import { submitFeedback } from '@/services/lexAgentClient';

interface LexFeedbackButtonsProps {
  conversationId: string;
  messageId: string;
  /** The user's question that produced this response */
  userQuestion: string;
  /** The response text the user is rating */
  responseText: string;
  /** Pipeline metadata for feedback classification */
  intentCategory?: string;
  toolId?: string;
  toolSuccess?: boolean;
}

type FeedbackState = 'none' | 'up' | 'down';

const LexFeedbackButtons: React.FC<LexFeedbackButtonsProps> = ({
  conversationId,
  messageId,
  userQuestion,
  responseText,
  intentCategory,
  toolId,
  toolSuccess,
}) => {
  const [feedback, setFeedback] = useState<FeedbackState>('none');
  const [showCorrection, setShowCorrection] = useState(false);
  const [correction, setCorrection] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRate = useCallback(
    async (rating: 'up' | 'down') => {
      if (submitting) return;

      // Toggle off if clicking same rating
      if (feedback === rating) {
        setFeedback('none');
        setShowCorrection(false);
        return;
      }

      setFeedback(rating);
      setSubmitting(true);

      try {
        await submitFeedback({
          conversationId,
          messageId,
          userQuestion,
          refinedResponse: responseText,
          rating,
          intentCategory,
          toolId,
          toolSuccess,
        });

        // Show correction input on thumbs-down
        if (rating === 'down') {
          setShowCorrection(true);
        } else {
          setShowCorrection(false);
        }
      } catch (err) {
        console.error('[LexFeedbackButtons] Failed to submit feedback:', err);
      } finally {
        setSubmitting(false);
      }
    },
    [conversationId, messageId, userQuestion, responseText, feedback, submitting, intentCategory, toolId, toolSuccess],
  );

  const handleSubmitCorrection = useCallback(async () => {
    if (!correction.trim() || submitting) return;
    setSubmitting(true);

    try {
      await submitFeedback({
        conversationId,
        messageId,
        userQuestion,
        refinedResponse: responseText,
        rating: 'down',
        correction: correction.trim(),
        intentCategory,
        toolId,
        toolSuccess,
      });
      setShowCorrection(false);
    } catch (err) {
      console.error('[LexFeedbackButtons] Failed to submit correction:', err);
    } finally {
      setSubmitting(false);
    }
  }, [conversationId, messageId, userQuestion, responseText, correction, submitting, intentCategory, toolId, toolSuccess]);

  // Determine if this looks like a tool failure for the visual hint
  const isLikelyToolFailure = toolSuccess === false || (
    toolId && responseText.length < 80 && /couldn't find|no results|no matching|unable to/i.test(responseText)
  );

  return (
    <div className="mt-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => handleRate('up')}
          disabled={submitting}
          className={`p-1 rounded-md transition-colors ${
            feedback === 'up'
              ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30'
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
          }`}
          title="Good response"
        >
          {feedback === 'up' ? (
            <HandThumbUpSolid className="w-4 h-4" />
          ) : (
            <HandThumbUpIcon className="w-4 h-4" />
          )}
        </button>
        <button
          type="button"
          onClick={() => handleRate('down')}
          disabled={submitting}
          className={`p-1 rounded-md transition-colors ${
            feedback === 'down'
              ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30'
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
          }`}
          title="Bad response"
        >
          {feedback === 'down' ? (
            <HandThumbDownSolid className="w-4 h-4" />
          ) : (
            <HandThumbDownIcon className="w-4 h-4" />
          )}
        </button>
        {feedback === 'up' && (
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">Thanks!</span>
        )}
        {/* Visual hint when tool failure detected — helps trainer know not to correct */}
        {feedback === 'down' && isLikelyToolFailure && (
          <span className="text-xs text-amber-500 dark:text-amber-400 ml-1" title="This looks like a tool/data failure, not a response quality issue. Corrections here won't improve Lex's responses — the tool pipeline needs fixing instead.">
            Tool issue detected
          </span>
        )}
      </div>

      {showCorrection && (
        <div className="mt-2">
          {isLikelyToolFailure && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-1.5">
              This appears to be a tool failure (missing/wrong data), not a phrasing issue. Corrections here train response formatting, not data retrieval. Only correct if the <em>wording</em> is wrong.
            </p>
          )}
          <div className="flex items-start gap-2">
            <input
              type="text"
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitCorrection()}
              placeholder="What should the response have been?"
              className="flex-1 text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-lex-accent"
            />
            <button
              type="button"
              onClick={handleSubmitCorrection}
              disabled={!correction.trim() || submitting}
              className="text-xs px-3 py-1.5 rounded-lg bg-lex-accent text-white hover:bg-lex-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LexFeedbackButtons;
